import { toast } from "react-toastify";
import { Chapter, ChapterContent, Course, CourseAvailabilityDto, CourseProgressViewDto, CourseStatus, CourseWaitlistEntry } from "../Types/CourseTypes";

import {
    apiGet,
    apiPost,
    apiPut,
    apiDelete,
    apiPublicGet,
    apiPublicPost,
    apiRawPut
} from "./apiClient";

/* =======================================================
   PUBLIC — Get all published courses
======================================================= */
export const GetAllPublishedCourses = async (): Promise<Course[] | null> => {
    try {
        return await apiPublicGet<Course[]>(`/courses`);
    } catch (err) {
        console.error("Error GetAllPublishedCourses:", err);
        toast.error("Tekniskt fel vid hämtning av kurser");
        return null;
    }
};

/* =======================================================
   ADMIN — Get all courses (protected)
======================================================= */
export const GetAllCoursesAdmin = async (): Promise<Course[] | null> => {
    try {
        return await apiGet<Course[]>(`/courses/admin`);
    } catch (err) {
        console.error("Error GetAllCoursesAdmin:", err);
        toast.error("Kunde inte hämta alla kurser");
        return null;
    }
};

export const GetCoursePreview = async (
  courseId: number
): Promise<CourseProgressViewDto> => {
  return await apiGet<CourseProgressViewDto>(
    `/courses/${courseId}/preview`
  );
};
/* =======================================================
   PUBLIC — Get course by ID
======================================================= */
export const GetCourseById = async (courseId: number): Promise<Course | null> => {
    try {
        return await apiPublicGet<Course>(`/courses/${courseId}`);
    } catch (err: any) {
        if (err?.status === 404) toast.warning("Kursen hittades inte");
        else toast.error("Fel vid hämtning av kurs");

        console.error("GetCourseById error", err);
        return null;
    }
};
/* =======================================================
   PROTECTED — Get course progress for logged-in user
======================================================= */
export const GetCourseProgress = async (
  courseId: number
): Promise<CourseProgressViewDto> => {
  return await apiGet<CourseProgressViewDto>(
    `/usercourse/${courseId}/progress`
  );
};
export const MarkChapterCompleted = async (
  courseId: number,
  chapterId: number
): Promise<void> => {
  await apiPost(`/UserCourse/${courseId}/chapter/${chapterId}/complete`);
};
/* =======================================================
   PROTECTED — Get course for logged-in user
======================================================= */
export const GetCourseForUser = async (courseId: number): Promise<Course | null> => {
    try {
        return await apiGet<Course>(`/courses/${courseId}/user`);
    } catch (err: any) {
        if (err?.status === 401) toast.warning("Du saknar tillgång till denna kurs");
        else toast.error("Fel vid hämtning av kurs");

        console.error("GetCourseForUser error", err);
        return null;
    }
};

/* =======================================================
   PROTECTED — Create Course
======================================================= */
export const CreateCourse = async (course: Partial<Course>): Promise<number | null> => {
    try {
        const res = await apiPost(`/courses`, course);

        if (!res) return null;

        toast.success("Kurs skapad!");
        return res.id;
    } catch (err) {
        console.error("CreateCourse error:", err);
        toast.error("Kunde inte skapa kurs");
        return null;
    }
};

/* =======================================================
   PROTECTED — Update Course
======================================================= */
export const UpdateCourse = async (course: Course): Promise<boolean> => {
    try {
        const res = await apiRawPut(`/courses/${course.id}`, course);

        if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }

        toast.success("Kurs uppdaterad");
        return true;
    } catch (err) {
        console.error("UpdateCourse error:", err);
        toast.error("Kunde inte uppdatera kursen");
        return false;
    }
};

/* =======================================================
   PROTECTED — Delete Course
======================================================= */
export const DeleteCourse = async (courseId: number): Promise<boolean> => {
    try {
        await apiDelete(`/courses/${courseId}`);
        toast.success("Kurs borttagen");
        return true;
    } catch (err) {
        console.error("DeleteCourse error:", err);
        toast.error("Kunde inte ta bort kurs");
        return false;
    }
};

/* =======================================================
   PROTECTED — Add Chapter
======================================================= */
export const AddChapter = async (
    courseId: number,
    chapter: Omit<Chapter, "id" | "contents">
): Promise<Course | null> => {
    try {
        const res = await apiPost<Course>(`/courses/${courseId}/chapters`, chapter);
        toast.success("Kapitel skapat");
        return res;
    } catch (err) {
        console.error("AddChapter error:", err);
        toast.error("Kunde inte skapa kapitel");
        return null;
    }
};

export const GetCourseAvailability = async (courseId: number): Promise<CourseAvailabilityDto> => {
    return await apiPublicGet<CourseAvailabilityDto>(`/courses/${courseId}/availability`);
};

export const UpdateChapter = async (
    chapterId: number,
    chapter: Chapter
): Promise<Course | null> => {
    try {
        const res = await apiPut<Course>(`/courses/chapters/${chapterId}`, chapter);
        toast.success("Kapitel uppdaterat");
        return res;
    } catch (err) {
        console.error("UpdateChapter error:", err);
        toast.error("Kunde inte uppdatera kapitlet");
        return null;
    }
};

export const DeleteChapter = async (chapterId: number): Promise<boolean> => {
    try {
        await apiDelete(`/courses/chapters/${chapterId}`);
        toast.success("Kapitel borttaget");
        return true;
    } catch (err) {
        console.error("DeleteChapter error:", err);
        toast.error("Kunde inte ta bort kapitlet");
        return false;
    }
};

/* =======================================================
   PROTECTED — Add Chapter Content
======================================================= */
export const AddChapterContent = async (
    chapterId: number,
    content: Omit<ChapterContent, "id">
): Promise<Course | null> => {
    try {
        const res = await apiPost<Course>(`/courses/chapters/${chapterId}/content`, content);
        toast.success("Innehåll tillagt");
        return res;
    } catch (err) {
        console.error("AddChapterContent error:", err);
        toast.error("Kunde inte lägga till innehåll");
        return null;
    }
};

export const UpdateChapterContent = async (
    contentId: number,
    content: ChapterContent
): Promise<Course | null> => {
    try {
        const res = await apiPut<Course>(`/courses/chapter-content/${contentId}`, content);
        toast.success("Kapitelinnehåll uppdaterat");
        return res;
    } catch (err) {
        console.error("UpdateChapterContent error:", err);
        toast.error("Kunde inte uppdatera kapitelinnehållet");
        return null;
    }
};

export const DeleteChapterContent = async (contentId: number): Promise<boolean> => {
    try {
        await apiDelete(`/courses/chapter-content/${contentId}`);
        toast.success("Kapitelinnehåll borttaget");
        return true;
    } catch (err) {
        console.error("DeleteChapterContent error:", err);
        toast.error("Kunde inte ta bort kapitelinnehållet");
        return false;
    }
};
export const UpdateCourseStatus = async (
  courseId: number,
  status: any
) => {
     
    console.log(courseId,status)
  await apiPut(`/courses/${courseId}/status`, { status:  status == 'Publicerad' ? 1 : status == 'Utkast' ? 0 : 2  });
};

export const GetCourseWaitlist = async (courseId: number): Promise<CourseWaitlistEntry[]> => {
    return await apiGet<CourseWaitlistEntry[]>(`/courses/${courseId}/waitlist`);
};

export const AddCourseWaitlistEntry = async (
    courseId: number,
    entry: Pick<CourseWaitlistEntry, "name" | "email">
): Promise<CourseWaitlistEntry> => {
    return await apiPost<CourseWaitlistEntry>(`/courses/${courseId}/waitlist`, entry);
};

export const AddPublicCourseWaitlistEntry = async (
    courseId: number,
    entry: Pick<CourseWaitlistEntry, "name" | "email">
): Promise<CourseWaitlistEntry> => {
    return await apiPublicPost<CourseWaitlistEntry>(`/courses/${courseId}/waitlist/public`, entry);
};

export const AcceptPublicCourseWaitlistEntry = async (token: string): Promise<CourseWaitlistEntry> => {
    return await apiPublicPost<CourseWaitlistEntry>(`/courses/waitlist/${encodeURIComponent(token)}/accept`);
};

export const DeleteCourseWaitlistEntry = async (entryId: number): Promise<void> => {
    await apiDelete(`/courses/waitlist/${entryId}`);
};


// export const CreateCourse = async (course: Course): Promise<number | null> => {
//     return await fetch(`${url}/courses`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         credentials: "include",
//         body: JSON.stringify(course)
//     })
//         .then(async res => {
//             if (res.status === 201) {
//                 toast.success("Kurs skapad!");
//                 const data = await res.json();
//                 return data.id;
//             } else {
//                 toast.error("Kunde inte skapa kurs");
//                 return null;
//             }
//         })
//         .catch(err => {
//             console.log("CreateCourse error:", err);
//             toast.error("Tekniskt fel");
//             return null;
//         });
// };

// export const UpdateCourse = async (course: Course): Promise<boolean> => {
//     return await fetch(`${url}/courses/${course.id}`, {
//         method: "PUT",
//         headers: { "Content-Type": "application/json" },
//         credentials: "include",
//         body: JSON.stringify(course)
//     })
//         .then(res => {
//             if (res.status === 204) {
//                 toast.success("Kurs uppdaterad");
//                 return true;
//             } else {
//                 toast.warning("Kunde inte uppdatera kursen");
//                 return false;
//             }
//         })
//         .catch(err => {
//             console.log("UpdateCourse error:", err);
//             toast.error("Tekniskt fel");
//             return false;
//         });
// };

// export const DeleteCourse = async (courseId: number): Promise<boolean> => {
//     return await fetch(`${url}/courses/${courseId}`, {
//         method: "DELETE",
//         credentials: "include"
//     })
//         .then(res => {
//             if (res.status === 204) {
//                 toast.success("Kurs borttagen");
//                 return true;
//             } else {
//                 toast.warning("Kunde inte ta bort kurs");
//                 return false;
//             }
//         })
//         .catch(err => {
//             console.log("DeleteCourse error:", err);
//             toast.error("Tekniskt fel");
//             return false;
//         });
// };

// /* ===================== CHAPTERS ===================== */

// export const AddChapter = async (courseId: number, chapter: Chapter): Promise<boolean> => {
//     return await fetch(`${url}/courses/${courseId}/chapters`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         credentials: "include",
//         body: JSON.stringify(chapter)
//     })
//         .then(res => {
//             if (res.status === 200) {
//                 toast.success("Kapitel skapat");
//                 return true;
//             } else {
//                 toast.warning("Kunde inte skapa kapitel");
//                 return false;
//             }
//         })
//         .catch(err => {
//             console.log("AddChapter error:", err);
//             toast.error("Tekniskt fel");
//             return false;
//         });
// };

// /* ===================== CONTENT ===================== */

// export const AddChapterContent = async (
//     chapterId: number,
//     content: ChapterContent
// ): Promise<boolean> => {
//     return await fetch(`${url}/courses/chapters/${chapterId}/content`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         credentials: "include",
//         body: JSON.stringify(content)
//     })
//         .then(res => {
//             if (res.status === 200) {
//                 toast.success("Innehåll tillagt");
//                 return true;
//             } else {
//                 toast.warning("Kunde inte lägga till innehåll");
//                 return false;
//             }
//         })
//         .catch(err => {
//             console.log("AddChapterContent error:", err);
//             toast.error("Tekniskt fel");
//             return false;
//         });
// };
